/**
 * A goal's picture in its tile: the Living Valley illustrations that replace
 * the emoji, which looked different on every phone. Tile size s, radius
 * round(s * .32), drawing at round(s * .74) -- see the handoff README.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { GoalIcon } from '../family/state';
import { GOAL_PICTURES } from './goalIcons';

export function GoalPicture({
  icon,
  size = 48,
  className,
}: {
  icon: GoalIcon | string;
  size?: number;
  className?: string;
}) {
  const art = GOAL_PICTURES[icon as GoalIcon] ?? GOAL_PICTURES.star;
  const inner = Math.round(size * 0.74);
  return (
    <span
      className={className ? `goal-picture ${className}` : 'goal-picture'}
      style={{ width: size, height: size, borderRadius: Math.round(size * 0.32) }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 48 48" width={inner} height={inner} dangerouslySetInnerHTML={{ __html: art }} />
    </span>
  );
}

/** The Birbly logo or mark, as the image the brand ships (never typeset). */
export function BrandLogo({
  kind = 'logo',
  reversed = false,
  height,
  className,
  alt = 'Birbly',
}: {
  kind?: 'logo' | 'mark';
  reversed?: boolean;
  height: number;
  className?: string;
  alt?: string;
}) {
  const src = `/brand/birbly-${kind}-v2${reversed && kind === 'logo' ? '-reversed' : ''}.webp`;
  // Intrinsic ratios of the shipped files, so the box is right before load.
  const ratio = kind === 'logo' ? 2048 / 768 : 1;
  return (
    <img
      className={className}
      src={src}
      alt={alt}
      height={height}
      width={Math.round(height * ratio)}
      decoding="async"
      draggable={false}
    />
  );
}

/**
 * The "⋯" menu on cards: a native <details>, so it works with no script and
 * a screen reader announces it as expandable.
 *
 * Audit fix, 2026-10-12: a <details> only closes when its own summary is
 * clicked, so an open menu used to stay open while you scrolled on, and
 * Escape did nothing. Now a tap anywhere outside, or Escape, closes it, and
 * Escape puts focus back on the ⋯ button.
 */
export function MoreMenu({
  label,
  children,
}: {
  /** Read by a screen reader, e.g. "More for Ice cream Friday". */
  label: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDetailsElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) ref.current.open = false;
    };
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !ref.current) return;
      ref.current.open = false;
      ref.current.querySelector('summary')?.focus();
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  return (
    <details className="more-menu" ref={ref} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary aria-label={label} title={label}>
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <circle cx="5" cy="12" r="2" fill="currentColor" />
          <circle cx="12" cy="12" r="2" fill="currentColor" />
          <circle cx="19" cy="12" r="2" fill="currentColor" />
        </svg>
      </summary>
      <div
        className="more-menu-list"
        onClick={(event) => {
          // Any choice closes the menu; the step it opens takes over.
          if ((event.target as HTMLElement).closest('button') && ref.current) ref.current.open = false;
        }}
      >
        {children}
      </div>
    </details>
  );
}
